import {
  PayOverTimeIntroResultTypenames,
  Query,
} from '@varo/graphql-schema-types';
import { logPayOverTimeError } from 'features/payOverTime/helpers/PayOverTime.helpers';
import { useRef, useState } from 'react';
import FetchPayOverTimeIntroDetails from 'services/graphql/queries/FetchPayOverTimeIntroDetails';
import { AbortError } from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useCancellableQuery from 'shared/hooks/useCancellableQuery/useCancellableQuery';

export enum PayOverTimeIntroStatus {
  Details = 'Details',
  Summary = 'Summary',
  Error = 'Error',
  Cancelled = 'Cancelled',
}

export type PayOverTimeIntroOutcome =
  | { status: PayOverTimeIntroStatus.Details }
  | { status: PayOverTimeIntroStatus.Summary }
  | {
      status: PayOverTimeIntroStatus.Error;
      title: string | null;
      message: string | null;
    }
  | { status: PayOverTimeIntroStatus.Cancelled };

const CANCELLED: PayOverTimeIntroOutcome = {
  status: PayOverTimeIntroStatus.Cancelled,
};

const toIntroOutcome = (
  data: Query,
  transactionId: string,
): PayOverTimeIntroOutcome => {
  const introResult = data.payOverTimeIntroDetails;

  if (
    introResult?.__typename ===
    PayOverTimeIntroResultTypenames.PayOverTimeIntroDetailsSuccess
  ) {
    return { status: PayOverTimeIntroStatus.Details };
  }

  if (
    introResult?.__typename ===
    PayOverTimeIntroResultTypenames.PayOverTimeIntroSummarySuccess
  ) {
    return { status: PayOverTimeIntroStatus.Summary };
  }

  const isIntroError =
    introResult?.__typename ===
    PayOverTimeIntroResultTypenames.PayOverTimeIntroError;

  const outcome: PayOverTimeIntroOutcome = {
    status: PayOverTimeIntroStatus.Error,
    title: isIntroError ? introResult.title : null,
    message: isIntroError ? introResult.message : null,
  };

  logPayOverTimeError(new Error('Pay Over Time intro details unavailable'), {
    transactionId,
    code: isIntroError ? introResult.code : null,
    title: outcome.title,
    message: outcome.message,
  });

  return outcome;
};

const usePayOverTimeIntroDetails = () => {
  const [fetchPayOverTimeIntroDetails] = useCancellableQuery<Query>(
    FetchPayOverTimeIntroDetails,
  );
  const [fetching, setFetching] = useState(false);
  const latestRequestIdRef = useRef(0);

  const fetchIntroDetails = async (
    transactionId: string,
  ): Promise<PayOverTimeIntroOutcome> => {
    latestRequestIdRef.current += 1;
    const requestId = latestRequestIdRef.current;
    setFetching(true);

    try {
      const data = await fetchPayOverTimeIntroDetails(
        { input: { transactionId } },
        { requestPolicy: 'network-only' },
      );

      return toIntroOutcome(data, transactionId);
    } catch (error) {
      if (error instanceof AbortError) {
        return CANCELLED;
      }

      logPayOverTimeError(error, {
        transactionId,
        code: null,
        title: null,
        message: null,
      });

      return {
        status: PayOverTimeIntroStatus.Error,
        title: null,
        message: null,
      };
    } finally {
      if (requestId === latestRequestIdRef.current) {
        setFetching(false);
      }
    }
  };

  return { fetchIntroDetails, fetching };
};

export default usePayOverTimeIntroDetails;
